import type { PrivateStackNavigatorParamList } from './types';
import type { NativeStackHeaderProps } from '@react-navigation/native-stack';

import { useCallback } from 'react';

import { Platform, Pressable, Text, View } from 'react-native';

import { useNavigation } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { AvoidSoftInput } from 'react-native-avoid-softinput';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useMedia } from 'tamagui';

import { Icon, getTestProps } from '@south-street-app/atoms';
import {
  AccountLinkScreen,
  AccountScreen,
  ArchivedChatScreen,
  ArchivedChatsScreen,
  ChatScreen,
  SupportChatScreen,
  UpdatesChatScreen,
} from '@south-street-app/screens';
import { isAndroid } from '@south-street-app/utils';

import { HomeTabNavigator } from '../HomeTabNavigator';
import { TradingStackNavigator } from '../TradingStackNavigator';
import {
  getMobileHeaderContainerStyle,
  getMobileHeaderLeftAbsoluteStyle,
  privateStackNavigatorStyles as styles,
} from './PrivateStackNavigator.styles';

const { Navigator, Screen } =
  createNativeStackNavigator<PrivateStackNavigatorParamList>();

const HeaderLeftButton = ({
  absolute = false,
  safeAreaTop = 0,
}: {
  absolute?: boolean;
  safeAreaTop?: number;
}) => {
  const { goBack } = useNavigation();
  const { desktop } = useMedia();
  const handleGoBack = useCallback(() => {
    // Workaround to avoid bottom sheet appearing on Android when using an input
    if (isAndroid) {
      AvoidSoftInput.setAdjustPan();
    }

    goBack();
  }, [goBack]);

  return (
    <Pressable
      accessibilityLabel={'Go back'}
      accessibilityRole={'button'}
      hitSlop={8}
      onPress={handleGoBack}
      style={({ pressed }) => [
        styles.headerLeft,
        desktop ? undefined : styles.mobileHeaderLeft,
        absolute ? getMobileHeaderLeftAbsoluteStyle(safeAreaTop) : undefined,
        pressed ? styles.pressStyle : undefined,
        Platform.OS === 'web' ? { cursor: 'pointer' } : undefined,
      ]}
      {...getTestProps('navigation-back-button')}
    >
      <Icon color={'$mono700'} size={'$6'} iconName={'chevronLeftIcon'} />
    </Pressable>
  );
};

const HeaderLeft = () => <HeaderLeftButton />;

const MobileHeader = ({
  title,
}: NativeStackHeaderProps & {
  title: string;
}) => {
  const { top } = useSafeAreaInsets();

  return (
    <View style={getMobileHeaderContainerStyle(top)}>
      <HeaderLeftButton absolute safeAreaTop={top} />
      {title ? <Text style={styles.mobileHeaderTitle}>{title}</Text> : null}
    </View>
  );
};

const renderMobileHeader =
  (title: string) => (props: NativeStackHeaderProps) =>
    <MobileHeader {...props} title={title} />;

const PrivateStackNavigator = () => {
  const { desktop } = useMedia();
  const getBackHeaderOptions = (title: string) =>
    desktop
      ? {
          headerLeft: HeaderLeft,
          headerTitle: title,
          headerTitleAlign: 'center' as const,
        }
      : {
          header: renderMobileHeader(title),
        };

  return (
    <Navigator screenOptions={{ headerShown: false }}>
      <Screen name={'HomeTab'} component={HomeTabNavigator} />
      <Screen name={'TradeScreens'} component={TradingStackNavigator} />
      <Screen
        name={'AccountScreen'}
        component={AccountScreen}
        options={{
          headerShown: true,
          ...getBackHeaderOptions('Account'),
          headerShadowVisible: false,
        }}
      />
      <Screen
        name={'AccountLinkScreen'}
        component={AccountLinkScreen}
        options={{
          headerShown: true,
          ...getBackHeaderOptions(''),
          headerShadowVisible: false,
        }}
      />
      <Screen
        name={'ChatScreen'}
        component={ChatScreen}
        options={{
          headerShown: desktop ? false : true,
          ...getBackHeaderOptions(''),
          headerShadowVisible: false,
        }}
      />
      <Screen
        name={'SupportChatScreen'}
        component={SupportChatScreen}
        options={{
          headerShown: desktop ? false : true,
          ...getBackHeaderOptions('Chat'),
          headerShadowVisible: false,
        }}
      />
      <Screen
        name={'UpdatesChatScreen'}
        component={UpdatesChatScreen}
        options={{
          headerShown: desktop ? false : true,
          ...getBackHeaderOptions('Updates'),
          headerShadowVisible: false,
        }}
      />
      <Screen
        name={'ArchivedChatsScreen'}
        component={ArchivedChatsScreen}
        options={{
          headerShown: desktop ? false : true,
          ...getBackHeaderOptions('Archived Chats'),
          headerShadowVisible: false,
        }}
      />
      <Screen
        name={'ArchivedChatScreen'}
        component={ArchivedChatScreen}
        options={{
          headerShown: desktop ? false : true,
          ...getBackHeaderOptions('Chat'),
          headerShadowVisible: false,
        }}
      />
    </Navigator>
  );
};

export { PrivateStackNavigator };
